/**
 * Commander Design System - Typography
 *
 * IBM Plex family for authentic industrial-control aesthetic.
 * Distinctive, functional, designed for mission-critical interfaces.
 */

export const fontFamily = {
  /** Primary UI font - IBM Plex Sans for clean, industrial feel */
  sans: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
  /** Monospace for IDs, DMR codes, technical values */
  mono: '"IBM Plex Mono", "SF Mono", Consolas, monospace',
  /** Condensed for headings and status displays */
  display: '"IBM Plex Sans Condensed", "IBM Plex Sans", sans-serif',
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const fontSize = {
  /** 11px - Tiny labels, node counts */
  xs: '0.6875rem',
  /** 13px - Small captions, secondary text */
  sm: '0.8125rem',
  /** 15px - Body text */
  md: '0.9375rem',
  /** 17px - Large body, emphasis */
  lg: '1.0625rem',
  /** 20px - Small headings */
  xl: '1.25rem',
  /** 24px - Section headings */
  '2xl': '1.5rem',
  /** 30px - Page titles */
  '3xl': '1.875rem',
  /** 36px - Hero text */
  '4xl': '2.25rem',
  /** 48px - Display */
  '5xl': '3rem',
} as const;

export const lineHeight = {
  /** Tight - headings, condensed text */
  tight: 1.2,
  /** Snug - subheadings */
  snug: 1.35,
  /** Normal - body text */
  normal: 1.5,
  /** Relaxed - large text blocks */
  relaxed: 1.65,
} as const;

export const letterSpacing = {
  /** Tight - large headings */
  tight: '-0.02em',
  /** Normal - body text */
  normal: '0',
  /** Wide - small caps, labels */
  wide: '0.02em',
  /** Wider - uppercase labels */
  wider: '0.05em',
  /** Widest - status indicators */
  widest: '0.1em',
} as const;

/** Kiosk-specific typography overrides for readability */
export const kioskTypography = {
  /** Base font size for kiosk UI (20px) */
  baseFontSize: '1.25rem',
  /** Minimum readable size (16px) */
  minFontSize: '1rem',
  /** Large button text (24px) */
  buttonFontSize: '1.5rem',
  /** Feedback message text (32px) */
  feedbackFontSize: '2rem',
} as const;

export const typography = {
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  letterSpacing,
  kiosk: kioskTypography,
} as const;
